<template>
  <div class="py-4 container-fluid">
    <div class="card">
      <div class="card-header pb-0">
        <div class="d-flex align-items-center mb-3" :class="{ 'flex-column align-items-stretch': isMobileDevice }">
          <h6 class="mb-0" :class="{ 'w-100 mb-2': isMobileDevice }">Órdenes</h6>
          <div class="d-flex align-items-center" :class="{ 'w-100': isMobileDevice, 'ms-auto': !isMobileDevice }" style="gap: 10px">
            <button
              v-if="isMobileDevice"
              type="button"
              class="btn btn-warning btn-sm"
              :class="{ 'w-100': isMobileDevice }"
              @click="toggleScanner"
            >
              <i class="fas fa-camera"></i>
              {{ isScanning ? '' : '' }}
            </button>
            <button
              v-if="isMobileDevice"
              type="button"
              class="btn btn-dark btn-sm w-100"
              @click="openDispatchModal"
              title="Despachados"
            >
              <i class="fas fa-truck"></i>
            </button>

            <!-- Botones individuales solo en desktop -->
            <template v-if="!isMobileDevice">
              <button
                type="button"
                class="btn btn-warning btn-sm"
                @click="openDispatchModal"
                title="Despachados"
              >
                <i class="fas fa-truck"></i>
              </button>
              <button
                type="button"
                class="btn btn-success btn-sm"
                :disabled="!selectedOrders.length"
                @click="openBarcodeModal"
              >
                <i class="fas fa-barcode me-1"></i>
                Generar códigos{{ selectedOrders.length ? ` (${selectedOrders.length})` : '' }}
              </button>
              <button
                type="button"
                class="btn btn-primary btn-sm"
                :disabled="isSyncing"
                @click="syncOrders"
              >
                <span v-if="isSyncing">
                  <i class="fas fa-spinner fa-spin me-1"></i>
                  Sincronizando{{ syncProgress.total > 0 ? ` (${syncProgress.total})` : '...' }}
                </span>
                <span v-else>Sincronizar</span>
              </button>
            </template>
          </div>
        </div>
        <div class="row g-2 align-items-end">
          <div class="col-12 col-lg-4">
            <label for="searchQuery" class="mb-1">Buscar</label>
            <input
              id="searchQuery"
              v-model="searchQuery"
              type="text"
              class="form-control"
              placeholder="Buscar por orden, cliente, email, departamento o ciudad..."
              @input="onSearchInput"
              @keyup.enter="onSearchEnter"
            />
          </div>
          <div class="col-6 col-md-4 col-lg-2">
            <label for="dateFromFilter" class="mb-1">Desde</label>
            <input
              id="dateFromFilter"
              v-model="dateFromFilter"
              type="date"
              class="form-control"
              @change="fetchOrders(1)"
            />
          </div>
          <div class="col-6 col-md-4 col-lg-2">
            <label for="dateToFilter" class="mb-1">Hasta</label>
            <input
              id="dateToFilter"
              v-model="dateToFilter"
              type="date"
              class="form-control"
              @change="fetchOrders(1)"
            />
          </div>
          <div class="col-12 col-md-4 col-lg-2">
            <label for="statusFilter" class="mb-1">Estado</label>
            <select
              id="statusFilter"
              v-model="statusFilter"
              class="form-select"
              @change="fetchOrders(1)"
            >
              <option value="">Todos</option>
              <option
                v-for="status in statusOptions"
                :key="status"
                :value="status"
              >
                {{ status }}
              </option>
            </select>
          </div>
          <div class="col-12 col-lg-2 d-flex" style="gap: 8px">
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm mb-0 filters-toggle"
              @click="clearFilters"
              title="Limpiar filtros"
            >
              <i class="fas fa-eraser"></i>
            </button>
            <button
              type="button"
              class="btn btn-sm mb-0 filters-toggle"
              :class="showMoreFilters ? 'btn-secondary' : 'btn-outline-secondary'"
              :aria-expanded="showMoreFilters"
              title="Más filtros"
              aria-label="Más filtros"
              @click="toggleMoreFilters"
            >
              <i class="fas fa-sliders-h"></i>
              <i class="fas ms-1" :class="showMoreFilters ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            </button>
          </div>
        </div>
        <div v-show="showMoreFilters" class="row g-2 mt-1 pt-2 border-top">
          <div class="col-12 col-md-3">
            <label for="paymentStatusFilter" class="mb-1">Pago</label>
            <select
              id="paymentStatusFilter"
              v-model="paymentStatusFilter"
              class="form-select"
              @change="fetchOrders(1)"
            >
              <option value="">Todos</option>
              <option value="paid">Pagado</option>
              <option value="not_paid">No pagado</option>
            </select>
          </div>
          <div class="col-12 col-md-3">
            <label for="paymentMethodFilter" class="mb-1">Método de pago</label>
            <select
              id="paymentMethodFilter"
              v-model="paymentMethodFilter"
              class="form-select"
              @change="fetchOrders(1)"
            >
              <option value="">Todos</option>
              <option v-for="method in paymentMethodOptions" :key="method" :value="method">{{ method }}</option>
            </select>
          </div>
          <div class="col-12 col-md-3">
            <label for="codFilter" class="mb-1">Contra entrega</label>
            <select
              id="codFilter"
              v-model="codFilter"
              class="form-select"
              @change="fetchOrders(1)"
            >
              <option value="">Todos</option>
              <option value="only">Solo contra entrega</option>
              <option value="hide">Ocultar contra entrega</option>
            </select>
          </div>
          <div class="col-12 col-md-3">
            <label for="departmentFilter" class="mb-1">Departamento</label>
            <select
              id="departmentFilter"
              v-model="departmentFilter"
              class="form-select"
              @change="onDepartmentChange"
            >
              <option value="">Todos</option>
              <option v-for="dept in destinationDepartments" :key="dept" :value="dept">{{ dept }}</option>
            </select>
          </div>
          <div class="col-12 col-md-3">
            <label for="cityFilter" class="mb-1">Ciudad</label>
            <input
              id="cityFilter"
              v-model="cityFilter"
              type="text"
              class="form-control"
              list="cityFilterOptions"
              placeholder="Todas"
              autocomplete="off"
              @input="onCityInput"
            />
            <datalist id="cityFilterOptions">
              <option v-for="city in destinationCityOptions" :key="city" :value="city"></option>
            </datalist>
          </div>
          <div class="col-12 col-md-3">
            <label for="printedFilter" class="mb-1">Código impreso</label>
            <select
              id="printedFilter"
              v-model="printedFilter"
              class="form-select"
              @change="fetchOrders(1)"
            >
              <option value="">Todos</option>
              <option value="printed">Ya impreso</option>
              <option value="not_printed">No impreso</option>
            </select>
          </div>
          <div class="col-12 col-md-3">
            <label for="syncFilter" class="mb-1">Sincronización</label>
            <select
              id="syncFilter"
              v-model="syncFilter"
              class="form-select"
              @change="fetchOrders(1)"
            >
              <option value="">Todos</option>
              <option value="pending">Pendiente por sincronizar</option>
            </select>
          </div>
        </div>
        <div class="pb-3"></div>
      </div>
      <div class="card-body px-0 pt-0 pb-2">
        <div v-if="isLoading" class="text-center py-4">
          <i class="fas fa-spinner fa-spin me-1"></i>
          Cargando órdenes...
        </div>

        <p v-else-if="!orders.length" class="text-muted px-4 mb-0">
          No hay órdenes. Pulsa Sincronizar para traerlas desde Shopify o registra ventas desde el POS.
        </p>

        <div v-else class="table-responsive">
          <table class="table align-items-center mb-0">
            <thead>
              <tr>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-4" style="width: 56px">
                  <input
                    type="checkbox"
                    style="width: 16px; height: 16px; margin: 0; cursor: pointer; accent-color: #344767;"
                    title="Seleccionar todos"
                    :checked="allOrdersSelected"
                    :indeterminate.prop="someOrdersSelected"
                    @change="toggleSelectAllOrders"
                  />
                </th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Orden
                </th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Cliente
                </th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Destino
                </th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Pago
                </th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Estado
                </th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Items
                </th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Total
                </th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Fecha
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="order in orders" :key="`${order.source}-${order.id}`">
                <td class="text-center ps-4">
                  <div class="d-flex align-items-center justify-content-center" style="gap: 10px">
                    <input
                      type="checkbox"
                      style="width: 16px; height: 16px; margin: 0; cursor: pointer; accent-color: #344767; flex-shrink: 0"
                      title="Seleccionar para imprimir lista de empaque"
                      :checked="isOrderSelected(order)"
                      @change="toggleOrderSelection(order)"
                    />
                    <span
                      v-if="isOrderSynced(order)"
                      class="btn btn-link btn-sm p-0 m-0 text-success d-flex align-items-center justify-content-center"
                      title="Sincronizado con Shopify"
                      style="cursor:default; width: 16px; height: 16px; line-height: 1"
                    >
                      <i class="fas fa-check-circle"></i>
                    </span>
                    <button
                      v-else-if="canSyncOrder(order)"
                      type="button"
                      class="btn btn-link btn-sm p-0 m-0 text-secondary d-flex align-items-center justify-content-center"
                      :disabled="syncingOrderId === order.id"
                      title="Enviar pedido a Shopify"
                      style="width: 16px; height: 16px; line-height: 1"
                      @click="syncPosOrder(order)"
                    >
                      <i
                        class="fas"
                        :class="syncingOrderId === order.id ? 'fa-spinner fa-spin' : 'fa-sync'"
                      ></i>
                    </button>
                    <button
                      v-if="order.source === 'pos'"
                      type="button"
                      class="btn btn-link btn-sm p-0 m-0 text-info d-flex align-items-center justify-content-center"
                      title="Ver ticket de venta"
                      style="width: 16px; height: 16px; line-height: 1"
                      @click="regenerateTicket(order)"
                    >
                      <i class="fas fa-receipt"></i>
                    </button>
                    <button
                      type="button"
                      class="btn btn-link btn-sm p-0 m-0 text-secondary d-flex align-items-center justify-content-center"
                      title="Ver detalles del pedido"
                      style="width: 16px; height: 16px; line-height: 1"
                      @click="viewOrderDetail(order)"
                    >
                      <i class="fas fa-eye"></i>
                    </button>
                  </div>
                </td>
                <td class="order-link-cell" title="Ver pedido" @click="viewOrderDetail(order)">
                  <span class="text-sm font-weight-bold d-block">{{ orderLabel(order) }}</span>
                  <span class="text-xs text-secondary">{{ orderSubtitle(order) }}</span>
                </td>
                <td>
                  <span class="text-sm font-weight-bold d-block">{{ orderCustomerName(order) }}</span>
                  <span v-if="orderCustomerEmail(order)" class="text-xs text-secondary">{{ orderCustomerEmail(order) }}</span>
                </td>
                <td>
                  <template v-if="orderDepartment(order) || orderCity(order)">
                    <span class="text-sm font-weight-bold d-block">{{ orderDepartment(order) || '—' }}</span>
                    <span class="text-xs text-secondary">{{ orderCity(order) || '—' }}</span>
                  </template>
                  <span v-else class="text-xs text-secondary">—</span>
                </td>
                <td class="text-center">
                  <span class="badge badge-sm" :class="paymentBadgeClass(order)">
                    {{ orderPaymentMethod(order) }}
                  </span>
                </td>
                <td class="text-center">
                  <span class="badge badge-sm" :class="orderStatusBadgeClass(order)">
                    {{ currentOrderStatus(order) }}
                  </span>
                  <span
                    v-if="order.barcode_printed_at"
                    class="badge badge-sm bg-gradient-dark ms-1"
                    :title="`Código impreso el ${formatDate(order.barcode_printed_at)}`"
                  >
                    <i class="fas fa-print me-1"></i>Impreso
                  </span>
                  <div v-if="currentOrderStatusDate(order)" class="status-date text-secondary">
                    {{ formatDate(currentOrderStatusDate(order)) }}
                    <i
                      class="fas fa-eye status-history-eye ms-1"
                      title="Ver historial de estados"
                      @click.stop="openStatusHistoryModal(order)"
                    ></i>
                  </div>
                </td>
                <td class="text-center">
                  <span class="text-sm">{{ orderLineItemsCount(order) }}</span>
                </td>
                <td class="text-center">
                  <span class="text-sm">{{ formatMoney(orderTotal(order), orderCurrency(order)) }}</span>
                </td>
                <td class="text-center">
                  <span class="text-xs text-secondary">{{ formatDate(orderDate(order)) }}</span>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="pagination p-3">
            <a
              v-for="link in links"
              :key="link.label"
              class="page-item cursor-pointer"
              :class="{ active: link.active, disabled: !link.url }"
              @click="link.url && fetchOrdersFromLink(link.url)"
            >
              {{ link.label }}
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Despachados: escanear/escribir pedido para marcarlo DESPACHADO -->
    <div v-if="showDispatchModal" class="barcode-modal-overlay dispatch-modal-overlay" @click.self="closeDispatchModal">
      <div class="barcode-modal card shadow">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="mb-0"><i class="fas fa-truck me-1"></i> Marcar pedidos como despachados</h6>
            <button type="button" class="btn-close" @click="closeDispatchModal"></button>
          </div>
          <label for="dispatchInput" class="mb-1">Número de pedido</label>
          <div class="d-flex" style="gap: 8px">
            <input
              id="dispatchInput"
              ref="dispatchInput"
              v-model="dispatchValue"
              type="text"
              inputmode="numeric"
              class="form-control"
              placeholder="Escanea el código de barras o escribe el número"
              :disabled="isDispatching"
              @keyup.enter="dispatchScannedOrder"
            />
            <button
              type="button"
              class="btn btn-warning mb-0"
              :disabled="isDispatching || !dispatchValue.trim()"
              @click="dispatchScannedOrder"
            >
              <i class="fas" :class="isDispatching ? 'fa-spinner fa-spin' : 'fa-check'"></i>
            </button>
            <button
              v-if="isMobileDevice"
              type="button"
              class="btn btn-outline-warning mb-0"
              :disabled="isDispatching"
              @click="startDispatchScanner"
            >
              <i class="fas fa-camera"></i>
            </button>
          </div>
          <div v-if="dispatchedLog.length" class="mt-3">
            <p class="text-xs text-muted mb-1">Despachados en esta sesión ({{ dispatchedLog.length }})</p>
            <ul class="list-unstyled text-sm mb-0 dispatch-log">
              <li v-for="entry in dispatchedLog" :key="entry.id">
                <i class="fas fa-check-circle text-success me-1"></i>
                {{ entry.label }} — {{ entry.customer }}
              </li>
            </ul>
          </div>
          <div class="d-flex justify-content-end mt-3">
            <button type="button" class="btn btn-secondary mb-0" :class="{ 'w-100': isMobileDevice }" @click="closeDispatchModal">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal historial de estados -->
    <div v-if="statusHistoryOrder" class="barcode-modal-overlay" @click.self="closeStatusHistoryModal">
      <div class="barcode-modal card shadow">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="mb-0">
              <i class="fas fa-history me-1"></i> Historial de estados
              <span class="text-secondary text-sm">{{ shopifyOrderNumber(statusHistoryOrder) }}</span>
            </h6>
            <button type="button" class="btn-close" @click="closeStatusHistoryModal"></button>
          </div>
          <div class="table-responsive status-history-table">
            <table class="table table-sm align-items-center mb-0">
              <thead>
                <tr>
                  <th class="text-xs text-secondary">Estado</th>
                  <th class="text-xs text-secondary">Fecha y hora</th>
                  <th class="text-xs text-secondary">Usuario</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(entry, index) in statusHistoryEntries" :key="index">
                  <td>
                    <span class="badge badge-sm" :class="statusBadgeClass(entry.status)">{{ entry.status }}</span>
                  </td>
                  <td class="text-sm">{{ formatDate(entry.date_at) }}</td>
                  <td class="text-sm">
                    <template v-if="entry.user_name">{{ entry.user_name }}</template>
                    <span v-else-if="index === 0" class="text-secondary">Creación del pedido</span>
                    <span v-else-if="'user_id' in entry" class="text-secondary">Sistema</span>
                    <span v-else class="text-secondary fst-italic">Sin registro de usuario</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="d-flex justify-content-end mt-3">
            <button type="button" class="btn btn-secondary btn-sm mb-0" @click="closeStatusHistoryModal">Cerrar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de escaneo de código de barras -->
    <div v-if="isScanning" class="scanner-modal-overlay" @click.self="stopScanner">
      <div class="scanner-modal card shadow">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="mb-0">Escanear código de barras</h6>
            <button type="button" class="btn-close" @click="stopScanner"></button>
          </div>
          <div class="scanner-video-wrapper">
            <video ref="scannerVideo" autoplay playsinline muted></video>
            <div class="scanner-guide"></div>
          </div>
          <div v-if="torchAvailable" class="text-center mt-2">
            <button type="button" class="btn btn-sm mb-0" :class="torchOn ? 'btn-warning' : 'btn-outline-secondary'" @click="toggleTorch">
              <i class="fas fa-lightbulb me-1"></i>{{ torchOn ? 'Apagar linterna' : 'Linterna' }}
            </button>
          </div>
          <p class="text-xs text-muted text-center mt-2 mb-0">Apunta la cámara al código de barras del pedido</p>
          <button type="button" class="btn btn-secondary w-100 mt-3 mb-0" @click="stopScanner">
            <i class="fas fa-times me-1"></i>Cerrar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de impresión de código de barras -->
    <div v-if="showBarcodeModal" class="barcode-modal-overlay" @click.self="closeBarcodeModal">
      <div class="barcode-modal card shadow">
        <div class="card-body">
          <h6 class="mb-3">Generar códigos de barras</h6>

          <div v-if="!barcodeItems.length" class="alert alert-warning py-2 text-sm">
            No se pudieron obtener los números de orden.
          </div>

          <template v-else>
            <p class="text-xs text-muted mb-2">Vista previa de la etiqueta (100 x 50 mm) — {{ barcodeItems.length }} pedido(s)</p>
            <div class="barcode-preview-wrapper">
              <div class="barcode-label" ref="labelPreview">
                <div class="barcode-label-customer">{{ barcodeItems[0]?.customerName || 'Cliente' }}</div>
                <div v-if="barcodeItems[0]?.city" class="barcode-label-city">{{ barcodeItems[0].city }}</div>
                <div class="barcode-label-body">
                  <svg ref="previewSvg"></svg>
                </div>
                <div class="barcode-label-number">{{ barcodeItems[0]?.barcode }}</div>
                <div v-if="barcodeItems[0]?.date" class="barcode-label-date">{{ barcodeItems[0].date }}</div>
              </div>
            </div>
          </template>

          <div class="d-flex gap-2 justify-content-end mt-3">
            <button type="button" class="btn btn-secondary btn-sm" @click="closeBarcodeModal">Cancelar</button>
            <button
              v-if="barcodeItems.length"
              type="button"
              class="btn btn-success btn-sm"
              @click="printBarcodes"
            >
              <i class="fas fa-print me-1"></i> Imprimir
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import shopifyService from "@/services/shopify.service";
import Swal from "sweetalert2";
import JsBarcode from 'jsbarcode';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { BarcodeFormat, DecodeHintType } from '@zxing/library';

export default {
  name: "ShopifyOrders",
  components: {
  },
  data() {
    return {
      isSyncing: false,
      isLoading: false,
      showDispatchModal: false,
      statusHistoryOrder: null,
      dispatchValue: "",
      isDispatching: false,
      dispatchedLog: [],
      selectedOrders: [],
      syncingOrderId: null,
      syncProgress: { total: 0, created: 0, updated: 0 },
      updatingStatusKey: null,
      statusOptions: [
        "NO PREPARADO",
        "PREPARADO",
        "APROBADO ENVIO",
        "GUIA GENERADA",
        "DESPACHADO",
        "ENTREGADO",
      ],
      orders: [],
      links: [],
      searchQuery: "",
      searchTimeout: null,
      statusFilter: "",
      paymentStatusFilter: "",
      codFilter: "",
      paymentMethodFilter: "",
      paymentMethodOptions: [],
      printedFilter: "",
      syncFilter: "",
      departmentFilter: "",
      cityFilter: "",
      destinationDepartments: [],
      destinationCities: {},
      showMoreFilters: false,
      dateFromFilter: "",
      dateToFilter: "",
      showBarcodeModal: false,
      barcodeItems: [],
      barcodeQuantity: 3,
      isScanning: false,
      isMobileDevice: false,
      scannerReader: null,
      scannerControls: null,
      scannerStream: null,
      // 'search' abre el pedido escaneado; 'dispatch' lo marca como despachado
      scannerTarget: 'search',
      scannerFrameId: null,
      torchAvailable: false,
      torchOn: false,
    };
  },
  computed: {
    destinationCityOptions() {
      if (this.departmentFilter) {
        return this.destinationCities[this.departmentFilter] || [];
      }
      return [...new Set(Object.values(this.destinationCities).flat())].sort((a, b) => a.localeCompare(b, "es"));
    },
    allOrdersSelected() {
      return this.orders.length > 0 && this.orders.every((order) => this.isOrderSelected(order));
    },
    statusHistoryEntries() {
      const history = this.statusHistoryOrder?.status_history;
      return Array.isArray(history) ? history : [];
    },
    someOrdersSelected() {
      const selected = this.orders.filter((order) => this.isOrderSelected(order)).length;
      return selected > 0 && selected < this.orders.length;
    },
  },
  mounted() {
    try {
      this.showMoreFilters = localStorage.getItem("shopifyOrders.showMoreFilters") === "1";
    } catch {
      this.showMoreFilters = false;
    }
    const savedPage = this.restoreFilters();
    this.fetchOrders(savedPage);
    this.fetchDestinations();
    this.isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || (window.innerWidth <= 1024 && 'ontouchstart' in window);
  },
  beforeUnmount() {
    this.stopScanner();
  },
  methods: {
    toggleMoreFilters() {
      this.showMoreFilters = !this.showMoreFilters;
      try {
        localStorage.setItem("shopifyOrders.showMoreFilters", this.showMoreFilters ? "1" : "0");
      } catch {
        // almacenamiento no disponible: solo se pierde la preferencia
      }
    },
    // Filtros y página se guardan en la sesión del navegador para conservarlos
    // al volver desde el detalle de un pedido.
    saveFilters(page) {
      try {
        sessionStorage.setItem("shopifyOrders.filters", JSON.stringify({
          page,
          searchQuery: this.searchQuery,
          statusFilter: this.statusFilter,
          paymentStatusFilter: this.paymentStatusFilter,
          codFilter: this.codFilter,
          paymentMethodFilter: this.paymentMethodFilter,
          printedFilter: this.printedFilter,
          syncFilter: this.syncFilter,
          departmentFilter: this.departmentFilter,
          cityFilter: this.cityFilter,
          dateFromFilter: this.dateFromFilter,
          dateToFilter: this.dateToFilter,
        }));
      } catch {
        // almacenamiento no disponible: solo se pierden los filtros al volver
      }
    },
    restoreFilters() {
      try {
        const saved = JSON.parse(sessionStorage.getItem("shopifyOrders.filters") || "null");
        if (!saved) return 1;
        const { page, ...filters } = saved;
        Object.keys(filters).forEach((key) => {
          if (key in this.$data && typeof filters[key] === "string") {
            this[key] = filters[key];
          }
        });
        return Number(page) > 0 ? Number(page) : 1;
      } catch {
        return 1;
      }
    },
    clearFilters() {
      this.searchQuery = "";
      this.statusFilter = "";
      this.paymentStatusFilter = "";
      this.codFilter = "";
      this.paymentMethodFilter = "";
      this.printedFilter = "";
      this.syncFilter = "";
      this.departmentFilter = "";
      this.cityFilter = "";
      this.dateFromFilter = "";
      this.dateToFilter = "";
      this.fetchOrders(1);
    },
    async fetchDestinations() {
      try {
        const { data } = await shopifyService.getOrderDestinations();
        this.destinationDepartments = data?.departments || [];
        this.destinationCities = data?.cities || {};
        this.paymentMethodOptions = data?.payment_methods || [];
      } catch (error) {
        console.error("Error al cargar departamentos y ciudades:", error);
      }
    },
    onDepartmentChange() {
      this.cityFilter = "";
      this.fetchOrders(1);
    },
    onCityInput() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.fetchOrders(1);
      }, 400);
    },
    orderDepartment(order) {
      return (order?.shipping_address?.province || "").trim();
    },
    orderCity(order) {
      return (order?.shipping_address?.city || "").trim();
    },
    onSearchInput() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.fetchOrders(1);
      }, 400);
    },
    onSearchEnter() {
      const value = this.searchQuery.trim();
      if (/^\d+$/.test(value)) {
        this.findAndRedirectToOrder(value);
      } else {
        this.fetchOrders(1);
      }
    },
    async findAndRedirectToOrder(orderNumber) {
      const num = String(orderNumber);

      // Primero buscar en la página actual por order_number o id
      // (los pedidos POS tienen order_number=NULL y se identifican por id)
      // Prioriza el número de venta de Shopify sobre el id interno.
      const findByNumber = () => this.orders.find((o) => this.shopifyOrderNumber(o) === num)
        || this.orders.find((o) => String(o.id) === num);
      const matched = findByNumber();
      if (matched) {
        this.viewOrderDetail(matched);
        return;
      }

      // Si no está en la página actual, hacer búsqueda al backend
      // y luego buscar coincidencia exacta en los resultados
      await this.fetchOrders(1);
      const exactMatch = findByNumber();
      if (exactMatch) {
        this.viewOrderDetail(exactMatch);
      }
    },
    async toggleScanner() {
      if (this.isScanning) {
        this.stopScanner();
      } else {
        await this.startScanner();
      }
    },
    async startDispatchScanner() {
      await this.startScanner('dispatch');
    },
    async startScanner(target = 'search') {
      this.scannerTarget = target;
      this.isScanning = true;
      this.torchOn = false;
      this.torchAvailable = false;
      this.$nextTick(async () => {
        try {
          const videoElement = this.$refs.scannerVideo;
          if (!videoElement) return;

          // Cámara trasera en alta resolución y con enfoque continuo: con más
          // píxeles por barra el código se lee de más lejos y con menos luz.
          const constraints = {
            audio: false,
            video: {
              facingMode: { ideal: 'environment' },
              width: { ideal: 1920 },
              height: { ideal: 1080 },
              advanced: [{ focusMode: 'continuous' }],
            },
          };

          if (await this.canUseNativeBarcodeDetector()) {
            await this.startNativeScanner(videoElement, constraints);
          } else {
            await this.startZxingScanner(videoElement, constraints);
          }
          this.detectTorchSupport(videoElement);
        } catch (error) {
          console.error('Error al iniciar el escáner:', error);
          Swal.fire('Error', 'No se pudo acceder a la cámara.', 'error');
          this.stopScanner();
        }
      });
    },
    handleScanResult(rawValue) {
      const scannedValue = String(rawValue || '').trim();
      if (!this.isScanning || !/^\d+$/.test(scannedValue)) return;
      const target = this.scannerTarget;
      this.stopScanner();
      if (target === 'dispatch') {
        this.dispatchValue = scannedValue;
        this.dispatchScannedOrder();
        return;
      }
      this.searchQuery = scannedValue;
      this.findAndRedirectToOrder(scannedValue);
    },
    async canUseNativeBarcodeDetector() {
      if (!('BarcodeDetector' in window)) return false;
      try {
        const supported = await window.BarcodeDetector.getSupportedFormats();
        return supported.includes('code_128');
      } catch (e) {
        return false;
      }
    },
    // Detector nativo (Chrome Android, Safari iOS 17+): mucho más rápido y
    // sensible que ZXing en JS, permite analizar casi cada frame.
    async startNativeScanner(videoElement, constraints) {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      this.scannerStream = stream;
      videoElement.srcObject = stream;
      await videoElement.play();

      const supported = await window.BarcodeDetector.getSupportedFormats();
      const formats = ['code_128', 'code_39', 'ean_13', 'ean_8', 'upc_a', 'itf']
        .filter((f) => supported.includes(f));
      const detector = new window.BarcodeDetector({ formats });

      const loop = async () => {
        if (!this.isScanning || this.scannerStream !== stream) return;
        try {
          if (videoElement.readyState >= 2) {
            const codes = await detector.detect(videoElement);
            if (codes.length) {
              this.handleScanResult(codes[0].rawValue);
              return;
            }
          }
        } catch (e) {
          // frame no disponible; se reintenta en el siguiente
        }
        this.scannerFrameId = requestAnimationFrame(loop);
      };
      this.scannerFrameId = requestAnimationFrame(loop);
    },
    async startZxingScanner(videoElement, constraints) {
      const hints = new Map();
      // Limitar formatos acelera cada intento y reduce falsos negativos
      hints.set(DecodeHintType.POSSIBLE_FORMATS, [
        BarcodeFormat.CODE_128,
        BarcodeFormat.CODE_39,
        BarcodeFormat.EAN_13,
        BarcodeFormat.EAN_8,
        BarcodeFormat.UPC_A,
        BarcodeFormat.ITF,
      ]);
      hints.set(DecodeHintType.TRY_HARDER, true);

      this.scannerReader = new BrowserMultiFormatReader(hints, {
        delayBetweenScanAttempts: 80,
      });

      this.scannerControls = await this.scannerReader.decodeFromConstraints(
        constraints,
        videoElement,
        (result) => {
          if (result) this.handleScanResult(result.getText());
        }
      );
    },
    detectTorchSupport(videoElement) {
      const stream = this.scannerStream || videoElement.srcObject;
      const track = stream && stream.getVideoTracks ? stream.getVideoTracks()[0] : null;
      const caps = track && track.getCapabilities ? track.getCapabilities() : {};
      this.torchAvailable = !!caps.torch;
    },
    async toggleTorch() {
      const videoElement = this.$refs.scannerVideo;
      const stream = this.scannerStream || (videoElement && videoElement.srcObject);
      const track = stream ? stream.getVideoTracks()[0] : null;
      if (!track) return;
      try {
        await track.applyConstraints({ advanced: [{ torch: !this.torchOn }] });
        this.torchOn = !this.torchOn;
      } catch (e) {
        console.error('No se pudo cambiar la linterna:', e);
      }
    },
    stopScanner() {
      if (this.scannerFrameId) {
        cancelAnimationFrame(this.scannerFrameId);
        this.scannerFrameId = null;
      }
      if (this.scannerStream) {
        this.scannerStream.getTracks().forEach((t) => t.stop());
        this.scannerStream = null;
      }
      if (this.scannerControls) {
        this.scannerControls.stop();
        this.scannerControls = null;
      }
      this.scannerReader = null;
      this.torchOn = false;
      this.torchAvailable = false;
      this.isScanning = false;
    },
    async fetchOrders(page = 1, { keepSelection = false } = {}) {
      this.isLoading = true;
      // Al cambiar de página (o recargar con filtros/búsqueda/sync) se limpia la
      // selección para no mantener checks en memoria de órdenes que ya no se ven.
      if (!keepSelection) {
        this.selectedOrders = [];
      }
      try {
        const response = await shopifyService.getOrders({
          page,
          search: this.searchQuery,
          status: this.statusFilter,
          payment_status: this.paymentStatusFilter,
          cod_filter: this.codFilter,
          payment_method: this.paymentMethodFilter,
          printed_filter: this.printedFilter,
          sync_filter: this.syncFilter,
          department: this.departmentFilter,
          city: this.cityFilter.trim(),
          date_from: this.dateFromFilter,
          date_to: this.dateToFilter,
          per_page: 60,
        });
        const data = response?.data || {};

        this.orders = data.data || [];
        this.links = data.links || [];
        // La página guardada puede ya no existir (p. ej. menos resultados al volver)
        if (!this.orders.length && page > 1) {
          return this.fetchOrders(1, { keepSelection });
        }
        this.saveOrdersNavigation();
        this.saveFilters(page);
      } catch (error) {
        console.error("Error al cargar órdenes Shopify:", error);
        await Swal.fire(
          "Error",
          error?.response?.data?.message || "No se pudieron cargar las órdenes.",
          "error"
        );
      } finally {
        this.isLoading = false;
      }
    },
    fetchOrdersFromLink(url) {
      const page = new URL(url).searchParams.get("page") || 1;
      this.fetchOrders(Number(page));
    },
    async syncOrders() {
      if (this.isSyncing) {
        return;
      }

      this.isSyncing = true;
      this.syncProgress = { total: 0, created: 0, updated: 0 };
      let pageInfo = null;

      try {
        do {
          const response = await shopifyService.syncOrders(pageInfo);
          const data = response?.data || {};

          if (!data.success) {
            throw new Error(data.message || "No se pudo sincronizar.");
          }

          const sync = data.sync || {};
          this.syncProgress.total += sync.total ?? 0;
          this.syncProgress.created += sync.created ?? 0;
          this.syncProgress.updated += sync.updated ?? 0;
          pageInfo = data.next_page_info || null;
        } while (pageInfo);

        await this.fetchOrders(1);
        await Swal.fire(
          "Sincronizado",
          `Se procesaron ${this.syncProgress.total} órdenes (${this.syncProgress.created} nuevas, ${this.syncProgress.updated} actualizadas).`,
          "success"
        );
      } catch (error) {
        console.error("Error al sincronizar órdenes Shopify:", error);
        await Swal.fire(
          "Error",
          error?.response?.data?.message || error.message || "Error al sincronizar órdenes con Shopify.",
          "error"
        );
      } finally {
        this.isSyncing = false;
      }
    },
    isOrderSelected(order) {
      return this.selectedOrders.some((item) => item.id === order.id);
    },
    // Pedidos seleccionados en el mismo orden en que aparecen en la tabla
    // (no en el orden en que se marcaron los checks).
    selectedOrdersInListOrder() {
      const listed = this.orders.filter((order) => this.isOrderSelected(order));
      const listedIds = new Set(listed.map((order) => order.id));
      const others = this.selectedOrders.filter((order) => !listedIds.has(order.id));
      return [...listed, ...others];
    },
    toggleOrderSelection(order) {
      if (this.isOrderSelected(order)) {
        this.selectedOrders = this.selectedOrders.filter((item) => item.id !== order.id);
      } else {
        this.selectedOrders.push(order);
      }
    },
    toggleSelectAllOrders() {
      if (this.allOrdersSelected) {
        const ids = new Set(this.orders.map((o) => o.id));
        this.selectedOrders = this.selectedOrders.filter((o) => !ids.has(o.id));
      } else {
        const existingIds = new Set(this.selectedOrders.map((o) => o.id));
        const toAdd = this.orders.filter((o) => !existingIds.has(o.id));
        this.selectedOrders = [...this.selectedOrders, ...toAdd];
      }
    },
    openDispatchModal() {
      this.showDispatchModal = true;
      this.dispatchValue = "";
      this.$nextTick(() => this.focusDispatchInput());
    },
    closeDispatchModal() {
      this.showDispatchModal = false;
      this.dispatchValue = "";
    },
    focusDispatchInput() {
      this.$refs.dispatchInput?.focus();
    },
    async findOrderByNumber(num) {
      // Misma búsqueda que el buscador: coincidencia exacta por order_number o id
      // (los pedidos POS tienen order_number=NULL y se identifican por id).
      const isMatch = (o) => String(o.order_number) === num || String(o.id) === num;
      const local = this.orders.find(isMatch);
      if (local) return local;
      const response = await shopifyService.getOrders({ page: 1, search: num, per_page: 20 });
      return (response?.data?.data || []).find(isMatch) || null;
    },
    async dispatchScannedOrder() {
      const num = this.dispatchValue.trim().replace(/^#/, "");
      if (!num || this.isDispatching) return;

      this.isDispatching = true;
      try {
        const order = await this.findOrderByNumber(num);
        if (!order) {
          await Swal.fire("No encontrado", `No se encontró el pedido ${num}.`, "warning");
          return;
        }

        const label = this.orderLabel(order);
        const status = this.currentOrderStatus(order);
        if (status === "DESPACHADO" || status === "ENTREGADO") {
          await Swal.fire(
            "Ya despachado",
            `El pedido ${label} ya fue ${status === "ENTREGADO" ? "despachado y entregado" : "despachado"}.`,
            "info"
          );
          return;
        }
        if (!["PREPARADO", "APROBADO ENVIO", "GUIA GENERADA"].includes(status)) {
          await Swal.fire("No preparado", `El pedido ${label} está en ${status}; aún no está preparado.`, "warning");
          return;
        }

        const response = await shopifyService.updateShippingStatus(order.id, "DESPACHADO");
        const data = response?.data || {};
        const idx = this.orders.findIndex((o) => o.id === order.id);
        if (idx !== -1 && data.status_history) {
          this.orders.splice(idx, 1, { ...this.orders[idx], status_history: data.status_history });
        }
        this.dispatchedLog.unshift({ id: `${order.id}-${Date.now()}`, label, customer: this.orderCustomerName(order) });
        await Swal.fire({
          icon: "success",
          title: "Despachado",
          text: data.dry_run
            ? `${label} marcado como DESPACHADO (modo simulación: no se actualizó Shopify).`
            : `${label} marcado como DESPACHADO.`,
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        await Swal.fire(
          "Error",
          error?.response?.data?.message || "No se pudo marcar el pedido como despachado.",
          "error"
        );
      } finally {
        this.isDispatching = false;
        this.dispatchValue = "";
        this.$nextTick(() => this.focusDispatchInput());
      }
    },
    isOrderSynced(order) {
      if (order?.source === "shopify" || order?.shopify_order_id) {
        return true;
      }

      return order?.shopify_product_id != null;
    },
    canSyncOrder(order) {
      return order?.source === "pos" && !this.isOrderSynced(order);
    },
    async syncPosOrder(order) {
      if (!this.canSyncOrder(order) || this.syncingOrderId !== null) {
        return;
      }

      const confirm = await Swal.fire({
        title: "Enviar a Shopify",
        text: `¿Deseas crear el pedido #${order.id} en Shopify?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Sí, sincronizar",
        cancelButtonText: "Cancelar",
      });

      if (!confirm.isConfirmed) {
        return;
      }

      this.syncingOrderId = order.id;

      try {
        const response = await shopifyService.syncPosSale(order.id);
        const data = response?.data || {};

        if (!data.success) {
          throw new Error(data.message || "No se pudo sincronizar la venta.");
        }

        await Swal.fire(
          "Sincronizado",
          `Pedido creado en Shopify: ${data.shopify_order_name || data.shopify_order_id}`,
          "success"
        );

        await this.fetchOrders();
      } catch (error) {
        await Swal.fire(
          "Error",
          error?.response?.data?.message || error.message || "No se pudo enviar el pedido a Shopify.",
          "error"
        );
      } finally {
        this.syncingOrderId = null;
      }
    },
    regenerateTicket(order) {
      if (order?.source !== 'pos') return;
      this.$router.push({ path: '/shopify/pos/ticket', query: { id: order.id } });
    },
    // Guarda el orden de la lista actual para que el detalle pueda pasar al
    // pedido siguiente/anterior deslizando en móvil.
    saveOrdersNavigation() {
      try {
        const nav = this.orders.map((o) => ({ source: o.source || 'shopify', id: o.id }));
        sessionStorage.setItem('shopifyOrdersNav', JSON.stringify(nav));
      } catch (e) {
        // sessionStorage no disponible: el deslizamiento simplemente no navega
      }
    },
    viewOrderDetail(order) {
      this.$router.push({
        name: 'Shopify Order Detail',
        query: { source: order.source || 'shopify', id: order.id },
      });
    },
    // Número de venta de Shopify (ej. 56878). El id interno de Mistock no se usa.
    shopifyOrderNumber(order) {
      if (order?.order_number) {
        return String(order.order_number);
      }
      const fromName = String(order?.title || "").match(/^#\s*(\d+)/);
      return fromName ? fromName[1] : null;
    },
    orderLabel(order) {
      const number = this.shopifyOrderNumber(order);
      return number ? `#${number}` : "Sin sincronizar";
    },
    orderSubtitle(order) {
      if (order?.source === "pos") {
        return "Venta POS";
      }

      return order?.shopify_order_id != null ? "Shopify" : (order?.title || "");
    },
    orderFulfillmentLabel(order) {
      if (order?.source === "pos") {
        return this.orderDeliveryLabel(order);
      }

      return order?.fulfillment_status || "—";
    },
    orderDeliveryLabel(order) {
      const method = this.orderVariants(order).delivery_method || "";

      return (
        {
          envio_nacional: "ENVIO NACIONAL",
          envio_local: "ENVIO LOCAL",
          efectivo: "EFECTIVO",
          retiro: "RETIRO EN TIENDA",
          domicilio: "DOMICILIO",
        }[method] || method.toUpperCase() || "POS"
      );
    },
    orderStatusKey(order) {
      return `${order.source}-${order.id}`;
    },
    currentOrderStatus(order) {
      const history = Array.isArray(order?.status_history) ? order.status_history : [];

      if (!history.length) {
        if (order?.fulfillment_status === "fulfilled") {
          return "PREPARADO";
        }
        return "NO PREPARADO";
      }

      return history[history.length - 1]?.status || "NO PREPARADO";
    },
    currentOrderStatusDate(order) {
      const history = Array.isArray(order?.status_history) ? order.status_history : [];
      return history.length ? history[history.length - 1]?.date_at || null : null;
    },
    openStatusHistoryModal(order) {
      this.statusHistoryOrder = order;
    },
    closeStatusHistoryModal() {
      this.statusHistoryOrder = null;
    },
    async updateOrderStatus(order, status) {
      if (!order?.source || !order?.id || status === this.currentOrderStatus(order)) {
        return;
      }

      const key = this.orderStatusKey(order);
      const source = order.source;
      const id = order.id;
      this.updatingStatusKey = key;

      try {
        const response = await shopifyService.updateOrderStatus(source, id, status);
        const data = response?.data || {};

        if (!data.success) {
          throw new Error(data.message || "No se pudo actualizar el estado.");
        }

        if (data.status_history) {
          const orderIndex = this.orders.findIndex(
            (item) => item.source === source && String(item.id) === String(id)
          );

          if (orderIndex !== -1) {
            this.orders.splice(orderIndex, 1, {
              ...this.orders[orderIndex],
              status_history: data.status_history,
            });
          }
        }
      } catch (error) {
        await Swal.fire(
          "Error",
          error?.response?.data?.message || error.message || "No se pudo actualizar el estado del pedido.",
          "error"
        );
        await this.fetchOrders();
      } finally {
        this.updatingStatusKey = null;
      }
    },
    orderVariants(order) {
      return order?.variants || {};
    },
    orderCustomerName(order) {
      return this.orderVariants(order).customer_name || order?.customer_name || "—";
    },
    orderCustomerEmail(order) {
      return this.orderVariants(order).customer_email || order?.email || "";
    },
    orderPaymentMethod(order) {
      const rawMethod =
        order?.payment_method ||
        this.orderVariants(order).payment_method ||
        order?.financial_status ||
        "";
      
      const method = String(rawMethod).trim();
      const methodLower = method.toLowerCase();
      
      // Map Shopify gateway names to readable names
      const methodNames = {
        // Shopify gateway names
        "COD": "Contra Entrega",
        "addi payment": "ADDI",
        "epayco": "ePayco",
        "TRB: abona $20.000 y paga el resto al recibir": "Contra Entrega",
        "wompi": "WOMPI",
        "manual": "Efectivo",
        // Internal codes
        contra_entrega: "Contra Entrega",
        efectivo: "Efectivo",
        EFECT: "Efectivo Cambio",
        transferencia: "Transferencia Bancaria",
        tarjeta_credito: "Tarjeta de Crédito",
        tarjeta_debito: "Tarjeta de Débito",
        nequi: "Nequi",
        daviplata: "Daviplata",
        pse: "PSE",
        pending: "Pendiente",
        paid: "Pagado",
      };
      
      // Si trae varios métodos separados por coma, mapear cada uno y unirlos
      if (method.includes(",")) {
        const parts = method
          .split(",")
          .map((part) => part.trim())
          .filter(Boolean)
          .map((part) => {
            const partLower = part.toLowerCase();
            return methodNames[part] || methodNames[partLower] || part.toUpperCase();
          });

        if (parts.length > 1) {
          return parts.join(" + ");
        }
      }

      // Try exact match first, then lowercase match
      return methodNames[method] || 
             methodNames[methodLower] || 
             method.toUpperCase() || 
             "—";
    },
    orderLineItemsCount(order) {
      const items = this.orderVariants(order).line_items;
      if (Array.isArray(items)) return items.length;
      return order?.line_items_count ?? order?.variants_count ?? 0;
    },
    orderTotal(order) {
      return this.orderVariants(order).total ?? order?.total_price ?? order?.price_max;
    },
    orderDate(order) {
      return order?.shopify_created_at || order?.synced_at;
    },
    financialStatusClass(status) {
      if (status === "paid") return "bg-gradient-success";
      if (status === "pending" || status === "partially_paid") return "bg-gradient-warning";
      if (status === "refunded" || status === "voided") return "bg-gradient-secondary";
      return "bg-gradient-info";
    },
    paymentBadgeClass(order) {
      if (!this.isOrderSynced(order)) {
        return "bg-gradient-warning";
      }
      return this.financialStatusClass(order.financial_status);
    },
    fulfillmentStatusClass(status) {
      if (status === "pos") return "bg-gradient-secondary";
      if (status === "fulfilled") return "bg-gradient-success";
      if (status === "partial") return "bg-gradient-warning";
      if (!status) return "bg-gradient-secondary";
      return "bg-gradient-info";
    },
    orderStatusBadgeClass(order) {
      return this.statusBadgeClass(this.currentOrderStatus(order));
    },
    statusBadgeClass(status) {
      if (status === "PREPARADO") return "bg-gradient-success";
      if (status === "ALISTANDO") return "bg-gradient-info";
      if (status === "APROBADO ENVIO") return "bg-gradient-warning";
      if (status === "GUIA GENERADA") return "bg-gradient-dark";
      if (status === "DESPACHADO") return "bg-gradient-primary";
      if (status === "ENTREGADO") return "bg-gradient-success";
      if (status === "CANCELADO") return "bg-gradient-danger";
      return "bg-gradient-secondary";
    },
    formatMoney(value, currency = "COP") {
      if (value == null) return "—";

      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: currency || "COP",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    },
    orderCurrency(order) {
      return order?.currency || "COP";
    },
    formatDate(value) {
      if (!value) return "—";

      return new Date(value).toLocaleString("es-CO", {
        dateStyle: "short",
        timeStyle: "short",
      });
    },
    openBarcodeModal() {
      const items = [];
      for (const order of this.selectedOrdersInListOrder()) {
        const orderNumber = this.shopifyOrderNumber(order);
        if (orderNumber) {
          const rawDate = this.orderDate(order);
          let dateStr = '';
          if (rawDate) {
            const d = new Date(rawDate);
            if (!isNaN(d.getTime())) {
              const dd = String(d.getDate()).padStart(2, '0');
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const yyyy = d.getFullYear();
              dateStr = `${dd}/${mm}/${yyyy}`;
            }
          }
          items.push({
            barcode: String(orderNumber).trim(),
            customerName: (order.customer_name || '').trim(),
            city: (order.shipping_address?.city || '').trim(),
            date: dateStr,
          });
        }
      }
      this.barcodeItems = items;
      this.showBarcodeModal = true;
      if (items.length) {
        this.$nextTick(() => this.renderPreviewBarcode());
      }
    },
    closeBarcodeModal() {
      this.showBarcodeModal = false;
    },
    // Marca los pedidos con la etiqueta "Impreso" sin cambiar su estado.
    async markOrdersBarcodePrinted(orders = []) {
      const ids = orders.filter((order) => order?.id).map((order) => order.id);
      if (!ids.length) return;

      try {
        const response = await shopifyService.markBarcodePrinted(ids);
        const printedAt = response?.data?.barcode_printed_at;
        if (!printedAt) return;
        const printedIds = new Set(ids.map(String));
        this.orders = this.orders.map((order) => (
          printedIds.has(String(order.id)) ? { ...order, barcode_printed_at: printedAt } : order
        ));
      } catch (error) {
        console.error('[markOrdersBarcodePrinted] Error:', error);
        Swal.fire(
          'No se marcaron como impresos',
          error?.response?.data?.message || 'No se pudo guardar el estado "Impreso" de los pedidos.',
          'error'
        );
      }
    },
    renderPreviewBarcode() {
      if (!this.$refs.previewSvg || !this.barcodeItems.length) return;
      try {
        JsBarcode(this.$refs.previewSvg, this.barcodeItems[0].barcode, {
          format: 'CODE128',
          displayValue: true,
          fontSize: 10,
          height: 45,
          margin: 1,
          width: 2,
        });
      } catch (e) {
        console.error('Error generando código de barras:', e);
      }
    },
    async printBarcodes() {
      // Rollo de etiquetas: filas de 3 tickets de 30 mm x 26 mm.
      // PAPER_WIDTH_MM es el ancho de papel configurado en la impresora; las
      // etiquetas se centran en ese ancho. OFFSET_X_MM corrige un corrimiento
      // físico de la impresora (+ mueve a la derecha, - a la izquierda).
      const COLUMNS = 3;
      const LABEL_WIDTH_MM = 30;
      const LABEL_HEIGHT_MM = 26;
      const PAPER_WIDTH_MM = 104;
      const OFFSET_X_MM = 0;
      const gapMm = (PAPER_WIDTH_MM - COLUMNS * LABEL_WIDTH_MM) / COLUMNS;

      const escapeHtml = (value) => String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

      const realLabels = [];
      for (const item of this.barcodeItems) {
        const tmpSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        try {
          JsBarcode(tmpSvg, item.barcode, {
            format: 'CODE128',
            displayValue: false,
            height: 60,
            margin: 0,
            width: 2,
          });
        } catch (e) {
          console.error('Error generando código de barras:', e);
          continue;
        }
        // Que el código ocupe todo el ancho/alto disponible de la etiqueta.
        const vbWidth = parseFloat(tmpSvg.getAttribute('width')) || 100;
        const vbHeight = parseFloat(tmpSvg.getAttribute('height')) || 60;
        tmpSvg.setAttribute('viewBox', `0 0 ${vbWidth} ${vbHeight}`);
        tmpSvg.setAttribute('preserveAspectRatio', 'none');
        tmpSvg.removeAttribute('width');
        tmpSvg.removeAttribute('height');
        tmpSvg.removeAttribute('style');
        const barcodeSvg = new XMLSerializer().serializeToString(tmpSvg);

        realLabels.push(`
          <div class="label">
            <div class="label-text label-customer">${escapeHtml(item.customerName)}</div>
            ${item.city ? `<div class="label-text">${escapeHtml(item.city)}</div>` : ''}
            <div class="label-body">${barcodeSvg}</div>
            <div class="label-text label-number">${escapeHtml(item.barcode)}</div>
            ${item.date ? `<div class="label-text label-date">${escapeHtml(item.date)}</div>` : ''}
          </div>`);
      }

      // Una fila (página) por cada 3 etiquetas; la última se completa con
      // etiquetas vacías para conservar la posición de las columnas.
      const rowsHtml = [];
      for (let i = 0; i < realLabels.length; i += COLUMNS) {
        const rowLabels = realLabels.slice(i, i + COLUMNS);
        while (rowLabels.length < COLUMNS) {
          rowLabels.push('<div class="label label-placeholder"></div>');
        }
        rowsHtml.push(`<div class="label-row">${rowLabels.join('')}</div>`);
      }
      const rows = rowsHtml.join('');

      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8" />
          <title>Imprimir códigos de barras</title>
          <style>
            @page { size: ${PAPER_WIDTH_MM}mm ${LABEL_HEIGHT_MM}mm; margin: 0; }
            * { box-sizing: border-box; }
            html, body { margin: 0; padding: 0; }
            .label-row {
              display: grid;
              grid-template-columns: repeat(${COLUMNS}, ${LABEL_WIDTH_MM}mm);
              column-gap: ${gapMm}mm;
              width: ${PAPER_WIDTH_MM}mm;
              height: ${LABEL_HEIGHT_MM}mm;
              padding-left: ${gapMm / 2 + OFFSET_X_MM}mm;
              overflow: hidden;
              page-break-after: always;
              break-after: page;
            }
            .label-row:last-child {
              page-break-after: auto;
              break-after: auto;
            }
            .label {
              width: ${LABEL_WIDTH_MM}mm;
              height: ${LABEL_HEIGHT_MM}mm;
              padding: 1.2mm 1mm;
              display: flex;
              flex-direction: column;
              align-items: center;
              font-family: Arial, Helvetica, sans-serif;
              overflow: hidden;
            }
            .label-placeholder {
              visibility: hidden;
            }
            .label-text {
              width: 100%;
              font-size: 6.5pt;
              font-weight: bold;
              line-height: 1.15;
              text-align: center;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .label-body {
              flex: 1;
              width: 100%;
              min-height: 0;
              padding: 0.6mm 0;
            }
            .label-body svg {
              display: block;
              width: 100%;
              height: 100%;
            }
            .label-number {
              font-size: 8pt;
            }
            .label-date {
              font-weight: normal;
            }
          </style>
        </head>
        <body onload="window.onafterprint = function(){ window.close(); }; setTimeout(function(){ window.print(); }, 100);">
          ${rows}
        </body>
        </html>`;

      const printWindow = window.open('', '_blank', 'width=800,height=600');
      if (!printWindow) {
        Swal.fire('Bloqueado', 'Permita las ventanas emergentes para imprimir.', 'warning');
        return;
      }
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();

      const ordersToUpdate = this.selectedOrdersInListOrder();
      this.closeBarcodeModal();
      this.markOrdersBarcodePrinted(ordersToUpdate);
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.pagination {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 20px;
}

.page-item {
  margin: 0 5px;
  padding: 4px 8px;
  user-select: none;
}

.page-item.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.active {
  font-weight: bold;
}

.order-status-select {
  min-width: 140px;
  font-size: 0.75rem;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
}

.barcode-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1080;
}

/* Debajo de SweetAlert (z-index 1060) para que sus alertas se vean encima */
.dispatch-modal-overlay {
  z-index: 1050;
}

.status-date {
  font-size: 0.65rem;
  margin-top: 2px;
  white-space: nowrap;
}

.status-history-eye {
  cursor: pointer;
}

.status-history-eye:hover {
  color: #344767;
}

.status-history-table {
  max-height: 360px;
  overflow-y: auto;
}

.filters-toggle {
  height: 40px;
  white-space: nowrap;
}

.dispatch-log {
  max-height: 220px;
  overflow-y: auto;
}

.barcode-modal {
  width: 100%;
  max-width: 480px;
  margin: 1rem;
}

.barcode-preview-wrapper {
  display: flex;
  justify-content: center;
  background: #f6f7f9;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 10px;
}

.barcode-label {
  width: 100mm;
  height: 50mm;
  max-width: 100%;
  background: #fff;
  border: 1px solid #dee2e6;
  padding: 4mm;
  display: flex;
  flex-direction: column;
  transform-origin: top center;
}

.barcode-label-customer {
  font-size: 10pt;
  font-weight: bold;
  text-align: left;
  width: 100%;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
}

.barcode-label-city {
  font-size: 8pt;
  text-align: left;
  width: 100%;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #555;
  margin-bottom: 2px;
}

.barcode-label-date {
  font-size: 8pt;
  text-align: center;
  width: 100%;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #555;
  margin-top: 2px;
}

.barcode-label-number {
  font-size: 11pt;
  font-weight: bold;
  text-align: center;
  width: 100%;
  margin-top: 4px;
}

.barcode-label-body {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.barcode-label-body svg {
  max-width: 60mm;
  max-height: 30mm;
}

.order-link-cell {
  cursor: pointer;
}

.order-link-cell:hover span:first-child {
  text-decoration: underline;
}

.scanner-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1080;
}

.scanner-modal {
  width: 100%;
  max-width: 400px;
  margin: 1rem;
}

.scanner-video-wrapper {
  position: relative;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
}

.scanner-video-wrapper video {
  width: 100%;
  height: auto;
  display: block;
}

.scanner-guide {
  position: absolute;
  left: 8%;
  right: 8%;
  top: 50%;
  height: 30%;
  transform: translateY(-50%);
  border: 2px solid rgba(255, 255, 255, 0.8);
  border-radius: 6px;
  pointer-events: none;
}
</style>
